// Чтение и изменение подотчёта. Ключи кэша с общим корнем, как у людей и объектов.
//
// Два решения, которые здесь важнее остального.
//
// **Прошлые данные не пропадают.** У списков и сводки стоит `placeholderData: keepPreviousData`.
// Это и есть техническая форма правила 11: переключили юрлицо — числа стоят на месте, пока не
// приедут новые. Спиннер на месте уже показанной суммы запрещён: человек видел число, дёрнул
// отбор и потерял его из виду — это читается как потеря денег, а не как обновление.
//
// **После денежного действия сбрасывается ВЕСЬ раздел, а не отдельный ключ.** Приёмка одной
// заявки трогает баланс двух людей, очередь, сводку, долг третьего и его строку в чужом
// разделе. Точечная инвалидация здесь — ловушка: что-нибудь обязательно забудут, и человек
// увидит старое число рядом с новым.

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  fetchArchive,
  fetchArchiveAuthors,
  fetchArchiveChats,
  type ArchiveChat,
  type ArchiveParams,
  type ArchiveSearch,
} from '@/shared/api/endpoints/chat-archive';

import {
  acceptClaim,
  bulkAcceptClaims,
  confirmTransfer,
  createClaim,
  declineTransfer,
  fetchCategories,
  fetchClaim,
  fetchClaims,
  fetchDashboard,
  fetchIssues,
  fetchMyAccountable,
  fetchPerson,
  fetchPersons,
  fetchReturns,
  fetchTransfers,
  registerIssue,
  updatePersonSettings,
  registerReturn,
  rejectClaim,
  returnClaim,
  reverseClaim,
  reverseIssue,
  reverseReturn,
  startTransfer,
  type BulkAcceptOut,
  type CategoryOut,
  type ClaimIn,
  type ClaimOut,
  type ClaimsQuery,
  type DashboardOut,
  type DashboardQuery,
  type IssueIn,
  type IssueListOut,
  type IssueOut,
  type IssuesQuery,
  type PersonCardOut,
  type PersonSettingsIn,
  type PersonOut,
  type PersonsQuery,
  type ReturnIn,
  type ReturnOut,
  type TransferIn,
  type TransferOut,
} from '@/shared/api/endpoints/accountable';
import type { ListResponse } from '@/shared/api/types';
import type {
  CashFlowQuery,
  CashFlowReportOut,
  CashTrendOut,
} from '@/shared/api/endpoints/reports';
import { fetchCashFlow, fetchCashTrend } from '@/shared/api/endpoints/reports';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

/** Справочник категорий меняется раз в месяц: держим дольше обычных тридцати секунд. */
const DICTIONARY_STALE_MS = 10 * 60 * 1000;

export const moneyKeys = {
  all: ['money'] as const,
  dashboard: (params: DashboardQuery) => ['money', 'dashboard', params] as const,
  persons: (params: PersonsQuery) => ['money', 'persons', params] as const,
  person: (personId: string) => ['money', 'person', personId] as const,
  me: () => ['money', 'me'] as const,
  categories: () => ['money', 'categories'] as const,
  issues: (params: IssuesQuery) => ['money', 'issues', params] as const,
  returns: (params: { person_id?: string }) => ['money', 'returns', params] as const,
  transfers: (params: { person_id?: string; status?: string }) =>
    ['money', 'transfers', params] as const,
  claims: (params: ClaimsQuery) => ['money', 'claims', params] as const,
  claim: (claimId: string) => ['money', 'claim', claimId] as const,
};

// ── Чтение ────────────────────────────────────────────────────────────────────────────────

export function useDashboard(
  params: DashboardQuery = {},
  включено = true,
): UseQueryResult<DashboardOut> {
  return useQuery({
    queryKey: moneyKeys.dashboard(params),
    queryFn: ({ signal }) => fetchDashboard(params, { signal }),
    enabled: включено,
    placeholderData: keepPreviousData,
  });
}

export function usePersons(params: PersonsQuery = {}): UseQueryResult<ListResponse<PersonOut>> {
  return useQuery({
    queryKey: moneyKeys.persons(params),
    queryFn: ({ signal }) => fetchPersons(params, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function usePerson(personId: string | null): UseQueryResult<PersonCardOut> {
  return useQuery({
    queryKey: moneyKeys.person(personId ?? ''),
    queryFn: ({ signal }) => fetchPerson(personId as string, { signal }),
    enabled: personId !== null,
  });
}

export function useMyAccountable(enabled = true): UseQueryResult<PersonCardOut> {
  return useQuery({
    queryKey: moneyKeys.me(),
    queryFn: ({ signal }) => fetchMyAccountable({ signal }),
    enabled,
  });
}

export function useCategories(): UseQueryResult<ListResponse<CategoryOut>> {
  return useQuery({
    queryKey: moneyKeys.categories(),
    queryFn: ({ signal }) => fetchCategories({ signal }),
    staleTime: DICTIONARY_STALE_MS,
  });
}

/**
 * Выдачи под отчёт. С `year` и `month` — реестр выдач за месяц («Кому выдано»): сервер
 * отвечает и строками, и суммой действующих выдач той же выборки. Прошлый месяц остаётся на
 * экране, пока едет новый: спиннер на месте показанной суммы запрещён (правило 11).
 */
export function useIssues(params: IssuesQuery, включено = true): UseQueryResult<IssueListOut> {
  return useQuery({
    queryKey: moneyKeys.issues(params),
    queryFn: ({ signal }) => fetchIssues(params, { signal }),
    enabled: включено,
    placeholderData: keepPreviousData,
  });
}

export function useReturns(params: { person_id?: string; with_reversed?: boolean }) {
  return useQuery({
    queryKey: moneyKeys.returns(params),
    queryFn: ({ signal }) => fetchReturns(params, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useTransfers(params: { person_id?: string; status?: string }) {
  return useQuery({
    queryKey: moneyKeys.transfers(params),
    queryFn: ({ signal }) => fetchTransfers(params, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useClaims(params: ClaimsQuery = {}): UseQueryResult<ListResponse<ClaimOut>> {
  return useQuery({
    queryKey: moneyKeys.claims(params),
    queryFn: ({ signal }) => fetchClaims(params, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useClaim(claimId: string | null): UseQueryResult<ClaimOut> {
  return useQuery({
    queryKey: moneyKeys.claim(claimId ?? ''),
    queryFn: ({ signal }) => fetchClaim(claimId as string, { signal }),
    enabled: claimId !== null,
  });
}

// ── Изменение ─────────────────────────────────────────────────────────────────────────────

/**
 * Сброс кэша после денежного действия.
 *
 * Целиком раздел «Суточные расходы», плюс люди (у получателя мог появиться долг и метка в реестре),
 * плюс расчёт (аванс в строке ведомости, когда она появится). Оптимистичного обновления
 * здесь нет ни у одного действия: все они двигают ЧУЖИЕ деньги, и «принято» до ответа
 * сервера было бы неправдой.
 */
function useСбросПослеДействия() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: moneyKeys.all });
    void client.invalidateQueries({ queryKey: ['people'] });
    void client.invalidateQueries({ queryKey: ['payroll'] });
  };
}

export function useUpdatePersonSettings(): UseMutationResult<
  PersonCardOut,
  unknown,
  { personId: string; body: PersonSettingsIn }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ personId, body }) => updatePersonSettings(personId, body),
    onSuccess: сбросить,
  });
}

export function useRegisterIssue(): UseMutationResult<
  IssueOut,
  unknown,
  { body: IssueIn; idempotencyKey: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }) => registerIssue(body, idempotencyKey),
    meta: подтверждение('Деньги выданы'),
    onSuccess: сбросить,
  });
}

export function useReverseIssue(): UseMutationResult<
  IssueOut,
  unknown,
  { issueId: string; basis: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ issueId, basis }) => reverseIssue(issueId, basis),
    meta: подтверждение('Выдача отменена'),
    onSuccess: сбросить,
  });
}

export function useReverseReturn(): UseMutationResult<
  ReturnOut,
  unknown,
  { returnId: string; basis: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ returnId, basis }) => reverseReturn(returnId, basis),
    meta: подтверждение('Возврат отменён'),
    onSuccess: сбросить,
  });
}

export function useRegisterReturn(): UseMutationResult<
  ReturnOut,
  unknown,
  { body: ReturnIn; idempotencyKey: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }) => registerReturn(body, idempotencyKey),
    meta: подтверждение('Возврат принят'),
    onSuccess: сбросить,
  });
}

export function useStartTransfer(): UseMutationResult<
  TransferOut,
  unknown,
  { body: TransferIn; idempotencyKey: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }) => startTransfer(body, idempotencyKey),
    meta: подтверждение('Передача отправлена: ждём подтверждения сменщика'),
    onSuccess: сбросить,
  });
}

export function useConfirmTransfer(): UseMutationResult<
  TransferOut,
  unknown,
  { transferId: string; idempotencyKey: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ transferId, idempotencyKey }) => confirmTransfer(transferId, idempotencyKey),
    meta: подтверждение('Передача принята'),
    onSuccess: сбросить,
  });
}

export function useDeclineTransfer(): UseMutationResult<
  TransferOut,
  unknown,
  { transferId: string; reason: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ transferId, reason }) => declineTransfer(transferId, reason),
    meta: подтверждение('Передача отклонена: деньги остались на том, кто передавал'),
    onSuccess: сбросить,
  });
}

export function useCreateClaim(): UseMutationResult<
  ClaimOut,
  unknown,
  { body: ClaimIn; idempotencyKey: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }) => createClaim(body, idempotencyKey),
    meta: подтверждение('Отчёт по чеку принят'),
    onSuccess: сбросить,
  });
}

export function useDecideClaim(): UseMutationResult<
  ClaimOut,
  unknown,
  { claimId: string; decision: 'accept' | 'reject' | 'return' | 'reverse'; reason?: string }
> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: ({ claimId, decision, reason }) => {
      if (decision === 'accept') {
        return acceptClaim(claimId);
      }
      if (decision === 'reject') {
        return rejectClaim(claimId, reason ?? '');
      }
      if (decision === 'return') {
        return returnClaim(claimId, reason ?? '');
      }
      return reverseClaim(claimId, reason ?? '');
    },
    // Четыре исхода на одну мутацию, и «Решение записано» вместо «Чек принят» — это отписка.
    meta: подтверждение<{ decision: 'accept' | 'reject' | 'return' | 'reverse' }>(
      ({ decision }) => {
        if (decision === 'accept') return 'Чек принят';
        if (decision === 'reject') return 'Чек отклонён: сумма осталась на человеке';
        if (decision === 'return') return 'Чек возвращён на доработку';
        return 'Приём чека отменён';
      },
    ),
    onSuccess: сбросить,
  });
}

export function useBulkAccept(): UseMutationResult<BulkAcceptOut, unknown, string[]> {
  const сбросить = useСбросПослеДействия();
  return useMutation({
    mutationFn: (claimIds: string[]) => bulkAcceptClaims(claimIds),
    // Подтверждения НЕТ намеренно: очередь отвечает сама, полосой над списком, и отвечает
    // подробнее — «Принято 4 на 47 850. Заведено долгов: 2. Пропущено 1: чек не приложен».
    // Всплывающее «Чеков принято: 4» рядом с этим — тот же факт, рассказанный дважды, причём
    // короткий вариант вытесняет подробный.
    onSuccess: сбросить,
  });
}

/**
 * Движение денег в полях по категориям (сценарий 62).
 *
 * Прошлый месяц остаётся на экране, пока едет выбранный: правило 11 запрещает спиннер поверх
 * уже показанной суммы — человек читает это как потерю данных, а не как обновление.
 */
export function useCashFlow(
  params: CashFlowQuery,
  включено = true,
): UseQueryResult<CashFlowReportOut> {
  return useQuery({
    queryKey: ['money', 'cash-flow', params],
    queryFn: () => fetchCashFlow(params),
    enabled: включено,
    placeholderData: keepPreviousData,
  });
}

/**
 * Деньги в полях по дням: выдано, возвращено, принято расходом.
 *
 * Календаря у полосы нет: она всегда про «сейчас» и кончается сегодняшним днём. Поэтому и
 * ключ кэша не содержит месяца — при листании месяца полоса не должна ни меняться, ни
 * перезапрашиваться.
 */
export function useCashTrend(
  params: { days?: number; legal_entity_id?: string; accountable_person_id?: string } = {},
): UseQueryResult<CashTrendOut> {
  return useQuery({
    queryKey: ['money', 'cash-trend', params],
    queryFn: () => fetchCashTrend(params),
    placeholderData: keepPreviousData,
  });
}

// ── Архив переписки ───────────────────────────────────────────────────────────────────────
//
// Живёт в разделе «Суточные расходы» по той же причине, по которой заказчик в него и пойдёт: в архиве
// авансы, зарплаты и номера карт, и открывают его из вопроса «сколько я выдал Позднякову».
// Чата в программе для компьютера нет вовсе, а заводить восьмой раздел ради одного экрана
// запрещено устройством продукта (ПРАВИЛА.md, раздел 1.1).

/**
 * Поиск по архиву. Страницы НАКАПЛИВАЮТСЯ: «Показать ещё» дописывает, а не заменяет.
 *
 * ## Почему страницами, а не растущим пределом
 *
 * Сначала было проще: просить `limit = 50 × страниц` и всегда с нуля. У сервера предел 200, и
 * четвёртое нажатие «Показать ещё» просило 250 — отказ, а вместе с ним пропадал ВЕСЬ уже
 * прочитанный список и вместо него вставал экран ошибки. Поймано разбором, а не тестом: до
 * четвёртой страницы ни один тест не доходил.
 *
 * ## Почему `keepPreviousData`
 *
 * Человек набирает фамилию по букве, и без него список мигал бы пустотой на каждом нажатии —
 * то есть выглядел бы так, будто ничего не нашлось, ровно когда он ещё дописывает слово.
 */
export function useArchiveSearch(
  params: Omit<ArchiveParams, 'offset'> & { limit: number },
): UseInfiniteQueryResult<InfiniteData<ArchiveSearch>> {
  return useInfiniteQuery({
    queryKey: ['money', 'archive', params],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchArchive({ ...params, offset: pageParam }),
    // Следующее смещение — это столько, сколько УЖЕ прочитано. Считать его как «номер
    // страницы × размер» нельзя: последняя страница бывает короче, и смещение уехало бы
    // за границу.
    getNextPageParam: (последняя, все) => {
      const прочитано = все.reduce((сумма, страница) => сумма + страница.items.length, 0);
      return прочитано < последняя.total ? прочитано : undefined;
    },
    placeholderData: keepPreviousData,
  });
}

/** Опись архива: какие чаты перенесены, сколько в каждом и за какой срок. */
export function useArchiveChats(): UseQueryResult<{ items: ArchiveChat[]; total: number }> {
  return useQuery({
    queryKey: ['money', 'archive', 'chats'],
    queryFn: () => fetchArchiveChats(),
  });
}

/** Кто писал: список для отбора по человеку, самые частые первыми. */
export function useArchiveAuthors(
  chat?: string,
): UseQueryResult<{ items: string[]; total: number }> {
  return useQuery({
    queryKey: ['money', 'archive', 'authors', chat ?? null],
    queryFn: () => fetchArchiveAuthors(chat),
  });
}
